
(() => {
  if (customElements.get('ideal-cash-pdf')) return;
  const pdfBase = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@6.2.108/';
  let libraryPromise;
  let unicodePromise;
  const getLibraries = () => {
    libraryPromise ||= Promise.all([
      import('https://cdn.jsdelivr.net/npm/pdfjs-dist@6.2.108/legacy/build/pdf.mjs'),
      import('https://cdn.jsdelivr.net/npm/pdf-lib@1.17.1/dist/pdf-lib.esm.js')
    ]).then(([pdfjs, pdfLib]) => {
      pdfjs.GlobalWorkerOptions.workerSrc = pdfBase + 'legacy/build/pdf.worker.mjs';
      return { pdfjs, pdfLib };
    }).catch(error => { libraryPromise = null; throw error; });
    return libraryPromise;
  };
  const readBytes = async (url, signal) => {
    const response = await fetch(url, { credentials: 'omit', signal });
    if (!response.ok) throw new Error('Resource unavailable');
    return new Uint8Array(await response.arrayBuffer());
  };
  const friendly = value => String(value || '').replaceAll('_', ' ').replace(/\b\w/g, letter => letter.toUpperCase());
  class IdealCashPdf extends HTMLElement {
    connectedCallback() {
      if (this.controller || !this.dataset.pdfUrl) return;
      if (!this.querySelector('[data-messages]')) {
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
        else queueMicrotask(() => { if (this.isConnected && this.querySelector('[data-messages]')) this.connectedCallback(); });
        return;
      }
      this.controller = new AbortController();
      this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
      this.viewer = this.querySelector('[data-viewer]');
      this.pagesElement = this.querySelector('[data-pages]');
      this.dialog = this.querySelector('[data-signature-dialog]');
      this.pad = this.querySelector('[data-pad]');
      this.statusElement = this.querySelector('[data-status]');
      this.dialogStatus = this.querySelector('[data-dialog-status]');
      this.zoom = 1;
      this.lastWidth = 0;
      this.values ||= new Map();
      if (this.previousPdfUrl && this.previousPdfUrl !== this.dataset.pdfUrl) {
        this.values.clear();
        this.signature = null;
        this.dirty = false;
      }
      this.previousPdfUrl = this.dataset.pdfUrl;
      this.renderQueue = Promise.resolve();
      this.renderTasks = new Set();
      const signal = this.controller.signal;
      this.addEventListener('click', event => this.handleAction(event), { signal });
      this.addEventListener('input', event => this.handleInput(event), { signal });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-upload]')) this.loadSignatureImage(event.target.files[0]);
      }, { signal });
      this.pad.addEventListener('pointerdown', event => this.startStroke(event), { signal });
      this.pad.addEventListener('pointermove', event => this.moveStroke(event), { signal });
      for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) {
        this.pad.addEventListener(type, event => this.endStroke(event), { signal });
      }
      window.addEventListener('beforeunload', event => {
        if (this.dirty) { event.preventDefault(); event.returnValue = ''; }
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.layoutPages().catch(() => this.status('load_error', true)), 120);
      });
      this.resizeObserver.observe(this.viewer);
      this.load();
    }
    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.controller?.abort();
        this.controller = null;
        this.resizeObserver?.disconnect();
        clearTimeout(this.resizeTimer);
        this.generation = (this.generation || 0) + 1;
        for (const task of this.renderTasks || []) task.cancel();
        this.loadingTask?.destroy().catch(() => {});
        if (this.dialog?.open) this.dialog.close();
        this.ready = false;
      });
    }
    text(key) { return this.messages[key] || key; }
    status(key, error = false) {
      this.statusElement.textContent = this.text(key);
      this.statusElement.dataset.error = String(error);
    }
    setBusy(value) {
      this.busy = value;
      this.viewer.inert = value;
      for (const button of this.querySelectorAll('.ideal-cash-pdf__toolbar button')) button.disabled = value || !this.ready;
    }
    async load() {
      const signal = this.controller.signal;
      this.ready = false;
      this.setBusy(true);
      this.status('loading');
      this.viewer.setAttribute('aria-busy', 'true');
      this.querySelector('[data-action="retry"]').hidden = true;
      this.pagesElement.replaceChildren();
      this.pages = [];
      this.controls = new Map();
      this.annotations = new Map();
      try {
        const url = new URL(this.dataset.pdfUrl, location.href);
        if (!['https:', 'http:'].includes(url.protocol)) throw new Error('template_error');
        const [libraries, bytes] = await Promise.all([getLibraries(), readBytes(url.href, signal)]);
        if (signal.aborted) return;
        this.pdfjs = libraries.pdfjs;
        this.pdfLib = libraries.pdfLib;
        this.template = bytes;
        this.loadingTask = this.pdfjs.getDocument({
          data: bytes.slice(), cMapUrl: pdfBase + 'cmaps/', cMapPacked: true,
          standardFontDataUrl: pdfBase + 'standard_fonts/', wasmUrl: pdfBase + 'wasm/',
          isEvalSupported: false, enableXfa: false
        });
        this.pdf = await this.loadingTask.promise;
        if (this.pdf.numPages < 2 || this.pdf.numPages > 10) throw new Error('template_error');
        let signatureAnnotation;
        let dateAnnotation;
        let applicantAnnotation;
        for (let index = 0; index < this.pdf.numPages; index++) {
          const page = await this.pdf.getPage(index + 1);
          const viewport = page.getViewport({ scale: 1 });
          const [annotations, content] = await Promise.all([page.getAnnotations({ intent: 'display' }), page.getTextContent()]);
          if (signal.aborted) return;
          const record = { page, viewport, annotations, index, accessibleText: content.items.map(item => item.str || '').join(' ') };
          this.pages.push(record);
          for (const annotation of annotations) {
            if (!annotation.fieldName) continue;
            const name = annotation.fieldName;
            if (name === 'applicant_signature') signatureAnnotation = { ...annotation, pageIndex: index };
            if (name === 'date_signed') dateAnnotation = { ...annotation, pageIndex: index };
            if (name === 'applicant_full_name') applicantAnnotation = { ...annotation, pageIndex: index };
            const group = this.annotations.get(name) || [];
            group.push({ ...annotation, pageIndex: index });
            this.annotations.set(name, group);
            if (!this.values.has(name)) {
              this.values.set(name, annotation.checkBox ? annotation.fieldValue === annotation.exportValue : annotation.fieldValue || '');
            }
          }
        }
        if (!this.annotations.has('legal_business_name') || !dateAnnotation || !applicantAnnotation || dateAnnotation.pageIndex !== applicantAnnotation.pageIndex) throw new Error('template_error');
        if (signatureAnnotation?.fieldType === 'Sig') throw new Error('template_error');
        this.signaturePlacement = signatureAnnotation ? { pageIndex: signatureAnnotation.pageIndex, rect: signatureAnnotation.rect } : {
          pageIndex: dateAnnotation.pageIndex,
          rect: [applicantAnnotation.rect[0], dateAnnotation.rect[1], dateAnnotation.rect[0] - 12, dateAnnotation.rect[3]]
        };
        const signaturePage = this.pages[this.signaturePlacement.pageIndex];
        const [sx, sy, sr, st] = this.signaturePlacement.rect;
        if (signaturePage.page.rotate !== 0 || sr - sx < 80 || st - sy < 20 || sx < 0 || sy < 0 || sr > signaturePage.viewport.width || st > signaturePage.viewport.height) throw new Error('template_error');
        for (const record of this.pages) this.createPage(record);
        this.syncDelivery();
        this.updateSignatureButton();
        this.ready = true;
        await this.layoutPages();
        if (!signal.aborted) this.status('ready');
      } catch (error) {
        if (signal.aborted) return;
        this.ready = false;
        this.status(error.message === 'template_error' ? 'template_error' : 'load_error', true);
        this.querySelector('[data-action="retry"]').hidden = false;
        this.loadingTask?.destroy().catch(() => {});
      } finally {
        if (!signal.aborted) {
          this.viewer.setAttribute('aria-busy', 'false');
          this.setBusy(false);
        }
      }
    }
    position(element, rect, viewport) {
      const [x1, y1] = viewport.convertToViewportPoint(rect[0], rect[1]);
      const [x2, y2] = viewport.convertToViewportPoint(rect[2], rect[3]);
      Object.assign(element.style, {
        left: `${Math.min(x1, x2) / viewport.width * 100}%`, top: `${Math.min(y1, y2) / viewport.height * 100}%`,
        width: `${Math.abs(x2 - x1) / viewport.width * 100}%`, height: `${Math.abs(y2 - y1) / viewport.height * 100}%`
      });
    }
    createPage(record) {
      const element = document.createElement('div');
      element.className = 'ideal-cash-pdf__page';
      element.setAttribute('role', 'group');
      element.setAttribute('aria-label', `${this.text('page')} ${record.index + 1} ${this.text('of')} ${this.pdf.numPages}`);
      const canvas = document.createElement('canvas');
      canvas.setAttribute('aria-hidden', 'true');
      const layer = document.createElement('div');
      layer.className = 'ideal-cash-pdf__fields';
      const accessibleText = document.createElement('div');
      accessibleText.className = 'ideal-cash-pdf__accessible-text';
      accessibleText.textContent = record.accessibleText;
      element.append(canvas, accessibleText, layer);
      this.pagesElement.append(element);
      Object.assign(record, { element, canvas, layer });
      for (const annotation of record.annotations) {
        const name = annotation.fieldName;
        if (name === 'applicant_signature') continue;
        if (!name) {
          if (annotation.url && /^(https?:|mailto:)/i.test(annotation.url)) {
            const link = document.createElement('a');
            link.className = 'ideal-cash-pdf__pdf-link';
            link.href = annotation.url;
            link.target = '_blank';
            link.rel = 'noopener';
            link.setAttribute('aria-label', annotation.url);
            this.position(link, annotation.rect, record.viewport);
            layer.append(link);
          }
          continue;
        }
        if (!['Tx', 'Btn'].includes(annotation.fieldType) || annotation.pushButton) throw new Error('template_error');
        const input = document.createElement(annotation.multiLine ? 'textarea' : 'input');
        input.className = 'ideal-cash-pdf__field';
        input.dataset.field = name;
        input.dataset.annotation = annotation.id;
        input.id = `${this.id}-${annotation.id}`;
        input.autocomplete = 'off';
        input.spellcheck = false;
        input.setAttribute('aria-label', annotation.alternativeText || friendly(name));
        if (annotation.fieldType === 'Btn') {
          input.type = annotation.radioButton ? 'radio' : 'checkbox';
          input.name = `${this.id}-${name}`;
          input.value = annotation.buttonValue || annotation.exportValue || 'Yes';
          input.checked = annotation.radioButton ? this.values.get(name) === annotation.buttonValue : !!this.values.get(name);
          if (annotation.radioButton) input.setAttribute('aria-label', `${this.text(name)}: ${friendly(input.value)}`);
        } else {
          if (!annotation.multiLine) input.type = 'text';
          input.value = this.values.get(name) || '';
          input.style.setProperty('--pdf-field-size', name === 'business_type_other' ? '8px' : '9px');
          if (annotation.maxLen > 0) input.maxLength = annotation.maxLen;
          if (/email/.test(name)) input.inputMode = 'email';
          if (/phone/.test(name)) input.inputMode = 'tel';
          if (/date/.test(name)) input.inputMode = 'text';
        }
        input.disabled = !!annotation.readOnly;
        this.position(input, annotation.rect, record.viewport);
        layer.append(input);
        const group = this.controls.get(name) || [];
        group.push(input);
        this.controls.set(name, group);
      }
      if (record.index === this.signaturePlacement.pageIndex) {
        this.signatureButton = document.createElement('button');
        this.signatureButton.type = 'button';
        this.signatureButton.className = 'ideal-cash-pdf__signature';
        this.signatureButton.dataset.action = 'sign';
        this.position(this.signatureButton, this.signaturePlacement.rect, record.viewport);
        const dateControl = this.controls.get('date_signed')?.[0];
        layer.insertBefore(this.signatureButton, dateControl || null);
      }
    }
    layoutPages() {
      if (!this.ready || !this.isConnected) return Promise.resolve();
      const width = Math.max(600, this.viewer.clientWidth - 24) * this.zoom;
      if (Math.abs(width - (this.lastWidth || 0)) < 1) return this.renderQueue;
      this.lastWidth = width;
      this.generation = (this.generation || 0) + 1;
      const generation = this.generation;
      for (const task of this.renderTasks) task.cancel();
      this.querySelector('[data-zoom]').textContent = `${Math.round(this.zoom * 100)}%`;
      for (const record of this.pages) {
        record.scale = width / record.viewport.width;
        record.element.style.width = `${width}px`;
        record.element.style.height = `${record.viewport.height * record.scale}px`;
        record.element.style.setProperty('--pdf-scale', record.scale);
      }
      this.renderQueue = this.renderQueue.catch(() => {}).then(async () => {
        for (const record of this.pages) {
          if (generation !== this.generation || !this.isConnected) return;
          const viewport = record.page.getViewport({ scale: record.scale });
          const ratio = Math.min(window.devicePixelRatio || 1, 1.5, Math.sqrt(2500000 / (viewport.width * viewport.height)));
          record.canvas.width = Math.ceil(viewport.width * ratio);
          record.canvas.height = Math.ceil(viewport.height * ratio);
          const task = record.page.render({
            canvasContext: record.canvas.getContext('2d'), viewport,
            transform: [ratio, 0, 0, ratio, 0, 0], annotationMode: this.pdfjs.AnnotationMode.DISABLE
          });
          this.renderTasks.add(task);
          try { await task.promise; } catch (error) {
            if (error.name !== 'RenderingCancelledException') throw error;
          } finally { this.renderTasks.delete(task); }
        }
      });
      return this.renderQueue;
    }
    handleInput(event) {
      const input = event.target.closest('[data-field]');
      if (!input || this.busy) return;
      const name = input.dataset.field;
      const value = input.type === 'checkbox' ? input.checked : input.value;
      this.values.set(name, value);
      for (const control of this.controls.get(name) || []) {
        control.removeAttribute('aria-invalid');
        if (control.type === 'radio') control.checked = control.value === value;
        else if (control.type === 'checkbox') control.checked = !!value;
        else if (control !== input) control.value = value;
      }
      if (name === 'delivery_same_as_billing' || name === 'billing_address') this.syncDelivery();
      this.dirty = true;
    }
    syncDelivery() {
      const same = !!this.values.get('delivery_same_as_billing');
      if (same) this.values.set('delivery_address', this.values.get('billing_address') || '');
      for (const control of this.controls.get('delivery_address') || []) {
        control.value = this.values.get('delivery_address') || '';
        control.readOnly = same;
      }
    }
    handleAction(event) {
      const button = event.target.closest('[data-action]');
      if (!button || !this.contains(button) || button.disabled) return;
      const action = button.dataset.action;
      if (action === 'download') this.download();
      if (action === 'retry') { this.lastWidth = 0; this.load(); }
      if (action === 'sign' && this.ready && !this.busy) this.openSignature();
      if (action === 'zoom-in' || action === 'zoom-out' || action === 'fit') {
        this.zoom = action === 'fit' ? 1 : Math.min(2.5, Math.max(.75, this.zoom + (action === 'zoom-in' ? .25 : -.25)));
        this.layoutPages().catch(() => this.status('load_error', true));
      }
      if (action === 'cancel') { this.imageGeneration = (this.imageGeneration || 0) + 1; this.dialog.close(); }
      if (action === 'clear') this.clearPad();
      if (action === 'upload') this.querySelector('[data-upload]').click();
      if (action === 'use-signature') this.useSignature();
      if (action === 'remove') {
        this.signature = null;
        this.dirty = true;
        this.updateSignatureButton();
        this.dialog.close();
        this.status('signature_removed');
      }
    }
    async openSignature() {
      const generation = this.imageGeneration = (this.imageGeneration || 0) + 1;
      this.strokes = [];
      this.draftImage = null;
      this.dialogStatus.textContent = '';
      this.dialogStatus.dataset.error = 'false';
      this.querySelector('[data-action="remove"]').hidden = !this.signature;
      this.querySelector('[data-upload]').value = '';
      this.dialog.showModal();
      this.drawPad();
      if (this.signature) {
        const image = new Image();
        image.src = this.signature.dataUrl;
        await image.decode();
        if (this.dialog.open && generation === this.imageGeneration) { this.draftImage = image; this.drawPad(); }
      }
    }
    clearPad() {
      this.imageGeneration = (this.imageGeneration || 0) + 1;
      this.strokes = [];
      this.activeStroke = null;
      this.draftImage = null;
      this.dialogStatus.textContent = '';
      this.drawPad();
    }
    point(event) {
      const rect = this.pad.getBoundingClientRect();
      return { x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)), y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)) };
    }
    startStroke(event) {
      if (event.button !== 0 || event.isPrimary === false || this.activeStroke) return;
      event.preventDefault();
      this.imageGeneration = (this.imageGeneration || 0) + 1;
      this.draftImage = null;
      this.activePointer = event.pointerId;
      this.activeStroke = [this.point(event)];
      this.strokes.push(this.activeStroke);
      this.pad.setPointerCapture(event.pointerId);
      this.dialogStatus.textContent = '';
      this.drawPad();
    }
    moveStroke(event) {
      if (!this.activeStroke || event.pointerId !== this.activePointer) return;
      event.preventDefault();
      const points = event.getCoalescedEvents?.();
      for (const item of points?.length ? points : [event]) this.activeStroke.push(this.point(item));
      this.drawPad();
    }
    endStroke(event) {
      if (event.pointerId !== this.activePointer) return;
      if (this.pad.hasPointerCapture(event.pointerId)) this.pad.releasePointerCapture(event.pointerId);
      this.activeStroke = null;
      this.activePointer = null;
    }
    drawPad() {
      const context = this.pad.getContext('2d');
      context.clearRect(0, 0, this.pad.width, this.pad.height);
      if (this.draftImage) {
        const ratio = Math.min((this.pad.width - 48) / this.draftImage.width, (this.pad.height - 48) / this.draftImage.height);
        const width = this.draftImage.width * ratio;
        const height = this.draftImage.height * ratio;
        context.drawImage(this.draftImage, (this.pad.width - width) / 2, (this.pad.height - height) / 2, width, height);
      }
      context.strokeStyle = '#17232e';
      context.fillStyle = '#17232e';
      context.lineWidth = 4;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      for (const stroke of this.strokes || []) {
        if (stroke.length < 2) continue;
        context.beginPath();
        context.moveTo(stroke[0].x * this.pad.width, stroke[0].y * this.pad.height);
        for (const point of stroke.slice(1)) context.lineTo(point.x * this.pad.width, point.y * this.pad.height);
        context.stroke();
      }
    }
    cropSignature(canvas) {
      const context = canvas.getContext('2d', { willReadFrequently: true });
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
      let left = canvas.width, top = canvas.height, right = -1, bottom = -1;
      for (let y = 0; y < canvas.height; y++) for (let x = 0; x < canvas.width; x++) {
        const index = (y * canvas.width + x) * 4;
        if (pixels.data[index + 3] < 20) continue;
        if (pixels.data[index] > 242 && pixels.data[index + 1] > 242 && pixels.data[index + 2] > 242) {
          pixels.data[index + 3] = 0;
          continue;
        }
        left = Math.min(left, x); top = Math.min(top, y); right = Math.max(right, x); bottom = Math.max(bottom, y);
      }
      if (right < left || bottom < top) return null;
      context.putImageData(pixels, 0, 0);
      left = Math.max(0, left - 3); top = Math.max(0, top - 3);
      right = Math.min(canvas.width - 1, right + 3); bottom = Math.min(canvas.height - 1, bottom + 3);
      const cropped = document.createElement('canvas');
      cropped.width = right - left + 1;
      cropped.height = bottom - top + 1;
      cropped.getContext('2d').drawImage(canvas, left, top, cropped.width, cropped.height, 0, 0, cropped.width, cropped.height);
      return { dataUrl: cropped.toDataURL('image/png'), width: cropped.width, height: cropped.height };
    }
    async loadSignatureImage(file) {
      if (!file) return;
      const generation = this.imageGeneration = (this.imageGeneration || 0) + 1;
      this.imageLoadingGeneration = generation;
      this.dialogStatus.textContent = this.text('image_loading');
      this.dialogStatus.dataset.error = 'false';
      let objectUrl;
      try {
        if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 5 * 1024 * 1024) throw new Error('image_error');
        objectUrl = URL.createObjectURL(file);
        const image = new Image();
        image.src = objectUrl;
        await image.decode();
        if (image.width * image.height > 12000000 || image.width > 6000 || image.height > 6000) throw new Error('image_error');
        const canvas = document.createElement('canvas');
        const ratio = Math.min(1, 1600 / image.width, 1000 / image.height);
        canvas.width = Math.max(1, Math.round(image.width * ratio));
        canvas.height = Math.max(1, Math.round(image.height * ratio));
        canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
        const signature = this.cropSignature(canvas);
        if (!signature) throw new Error('image_empty');
        const cropped = new Image();
        cropped.src = signature.dataUrl;
        await cropped.decode();
        if (generation !== this.imageGeneration || !this.dialog.open) return;
        this.strokes = [];
        this.draftImage = cropped;
        this.drawPad();
        this.dialogStatus.textContent = this.text('image_ready');
      } catch (error) {
        if (generation !== this.imageGeneration) return;
        this.dialogStatus.textContent = this.text(error.message === 'image_empty' ? 'image_empty' : 'image_error');
        this.dialogStatus.dataset.error = 'true';
      } finally {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        if (this.imageLoadingGeneration === generation) this.imageLoadingGeneration = null;
        this.querySelector('[data-upload]').value = '';
      }
    }
    useSignature() {
      if (this.imageLoadingGeneration && this.imageLoadingGeneration === this.imageGeneration) {
        this.dialogStatus.textContent = this.text('image_loading');
        return;
      }
      const signature = this.cropSignature(this.pad);
      if (!signature) {
        this.dialogStatus.textContent = this.text('draw_first');
        this.dialogStatus.dataset.error = 'true';
        return;
      }
      this.signature = signature;
      this.dirty = true;
      this.updateSignatureButton();
      this.dialog.close();
      this.status('signature_added');
    }
    updateSignatureButton() {
      if (!this.signatureButton) return;
      this.signatureButton.replaceChildren();
      this.signatureButton.dataset.signed = String(!!this.signature);
      this.signatureButton.setAttribute('aria-label', this.text(this.signature ? 'change_signature' : 'tap_sign'));
      if (this.signature) {
        const image = document.createElement('img');
        image.src = this.signature.dataUrl;
        image.alt = this.text('signature_preview');
        image.width = this.signature.width;
        image.height = this.signature.height;
        this.signatureButton.append(image);
      } else this.signatureButton.textContent = this.text('tap_sign');
    }
    invalidField(name, message) {
      const input = this.controls.get(name)?.[0];
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        input.scrollIntoView({ block: 'center', inline: 'center' });
        input.focus({ preventScroll: true });
      }
      this.status(message, true);
    }
    fits(text, font, size, annotation) {
      const width = annotation.rect[2] - annotation.rect[0] - 6;
      const height = annotation.rect[3] - annotation.rect[1] - 4;
      if (!annotation.multiLine) return font.widthOfTextAtSize(text, size) <= width;
      let lines = 0;
      for (const paragraph of text.split(/\r\n?|\n/)) {
        if (!paragraph) { lines++; continue; }
        let current = '';
        for (const word of paragraph.split(/(\s+)/)) {
          if (font.widthOfTextAtSize(current + word, size) <= width) current += word;
          else {
            if (current.trim()) lines++;
            current = word.trimStart();
            if (font.widthOfTextAtSize(current, size) > width) return false;
          }
        }
        lines++;
      }
      return lines * size * 1.2 <= height;
    }
    normalizeReferences(pdf) {
      const { PDFRef, PDFDict, PDFArray } = this.pdfLib;
      const objects = pdf.context.enumerateIndirectObjects();
      const replacements = new Map();
      for (const [reference] of objects) if (reference.generationNumber !== 0) replacements.set(reference.toString(), pdf.context.nextRef());
      if (!replacements.size) return;
      const visited = new Set();
      const rewrite = value => {
        if (value instanceof PDFRef) return replacements.get(value.toString()) || value;
        if (!value || typeof value !== 'object' || visited.has(value)) return value;
        visited.add(value);
        if (value instanceof PDFDict) for (const [key, child] of value.entries()) value.set(key, rewrite(child));
        else if (value instanceof PDFArray) for (let index = 0; index < value.size(); index++) value.set(index, rewrite(value.get(index)));
        else if (value.dict instanceof PDFDict) rewrite(value.dict);
        return value;
      };
      for (const [reference, object] of objects) {
        rewrite(object);
        const replacement = replacements.get(reference.toString());
        if (replacement) { pdf.context.assign(replacement, object); pdf.context.delete(reference); }
      }
      for (const [key, value] of Object.entries(pdf.context.trailerInfo)) pdf.context.trailerInfo[key] = rewrite(value);
    }
    async download() {
      if (!this.ready || this.busy) return;
      const legalName = String(this.values.get('legal_business_name') || '').trim();
      if (!legalName || /^n\/?a$/i.test(legalName)) { this.invalidField('legal_business_name', 'legal_required'); return; }
      if (!this.signature) { this.status('signature_required', true); this.openSignature(); return; }
      this.syncDelivery();
      this.setBusy(true);
      this.status('saving');
      let invalid;
      try {
        const { PDFDocument, StandardFonts, PDFTextField, PDFCheckBox, PDFRadioGroup, PDFName, PDFBool, PDFDict } = this.pdfLib;
        const pdf = await PDFDocument.load(this.template.slice());
        this.normalizeReferences(pdf);
        const form = pdf.getForm();
        const oldSignature = form.getFieldMaybe('applicant_signature');
        if (oldSignature) form.removeField(oldSignature);
        const normalFont = await pdf.embedFont(StandardFonts.Helvetica);
        let resources = form.acroForm.dict.lookup(PDFName.of('DR'), PDFDict);
        if (!resources) { resources = pdf.context.obj({}); form.acroForm.dict.set(PDFName.of('DR'), resources); }
        let fonts = resources.lookup(PDFName.of('Font'), PDFDict);
        if (!fonts) { fonts = pdf.context.obj({}); resources.set(PDFName.of('Font'), fonts); }
        fonts.set(PDFName.of(normalFont.name), normalFont.ref);
        let unicodeFont;
        const supported = new Set(normalFont.getCharacterSet());
        for (const field of form.getFields()) {
          const name = field.getName();
          if (!this.values.has(name)) continue;
          const value = this.values.get(name);
          if (field instanceof PDFTextField) {
            const text = String(value || '');
            const size = name === 'business_type_other' ? 8 : 9;
            let font = normalFont;
            if ([...text].some(character => !/[\r\n]/.test(character) && !supported.has(character.codePointAt(0)))) {
              unicodePromise ||= Promise.all([
                import('https://cdn.jsdelivr.net/npm/@pdf-lib/fontkit@1.1.1/+esm'),
                readBytes('https://cdn.jsdelivr.net/gh/notofonts/noto-fonts@v20201206-phase3/hinted/ttf/NotoSans/NotoSans-Regular.ttf')
              ]).catch(error => { unicodePromise = null; throw error; });
              const [fontkit, fontBytes] = await unicodePromise;
              pdf.registerFontkit(fontkit.default);
              unicodeFont ||= await pdf.embedFont(fontBytes, { subset: false });
              font = unicodeFont;
              fonts.set(PDFName.of(font.name), font.ref);
              const glyphs = new Set(font.getCharacterSet());
              if ([...text].some(character => !/[\r\n]/.test(character) && !glyphs.has(character.codePointAt(0)))) {
                invalid = { name, message: 'character_error' }; throw new Error('invalid_field');
              }
            }
            for (const annotation of this.annotations.get(name) || []) {
              if (text && !this.fits(text, font, size, annotation)) {
                invalid = { name, message: 'overflow' }; throw new Error('invalid_field');
              }
            }
            field.setText(text);
            for (const widget of field.acroField.getWidgets()) widget.dict.delete(PDFName.of('DA'));
            field.acroField.setDefaultAppearance(`/${font.name} ${size} Tf 0.113725 0.160784 0.223529 rg`);
            field.updateAppearances(font);
          } else if (field instanceof PDFCheckBox) {
            value ? field.check() : field.uncheck();
            field.updateAppearances();
          } else if (field instanceof PDFRadioGroup) {
            if (value && value !== 'Off') field.select(value);
            else field.clear();
            field.updateAppearances();
          }
        }
        form.acroForm.dict.set(PDFName.of('NeedAppearances'), PDFBool.False);
        const signatureImage = await pdf.embedPng(this.signature.dataUrl);
        const [left, bottom, right, top] = this.signaturePlacement.rect;
        const ratio = Math.min((right - left - 8) / signatureImage.width, (top - bottom - 8) / signatureImage.height);
        const width = signatureImage.width * ratio;
        const height = signatureImage.height * ratio;
        pdf.getPage(this.signaturePlacement.pageIndex).drawImage(signatureImage, {
          x: left + (right - left - width) / 2, y: bottom + (top - bottom - height) / 2, width, height
        });
        const bytes = await pdf.save({ updateFieldAppearances: false, useObjectStreams: false });
        const safeName = legalName.normalize('NFC').replace(/[\u0000-\u001f\u007f<>:"/\\|?*]+/g, ' ').replace(/\s+/g, ' ').replace(/^[.\s]+|[.\s]+$/g, '').slice(0, 80).trim() || 'Applicant';
        const objectUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `Ideal Electrical Cash Account Application - ${safeName}.pdf`;
        document.body.append(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
        this.dirty = false;
        this.status('saved');
      } catch (error) {
        if (!invalid) this.status('save_error', true);
      } finally {
        this.setBusy(false);
        if (invalid) this.invalidField(invalid.name, invalid.message);
      }
    }
  }
  customElements.define('ideal-cash-pdf', IdealCashPdf);
})();